/**
 * Question preview component supporting all 11 question types.
 */
import React from 'react';
import {
  CheckCircle2,
  Circle,
  Edit3,
  ArrowRight,
  Link2,
  List,
  CreditCard,
  Mic,
  Zap,
  MessageSquare,
  FileText,
} from 'lucide-react';
import { Question } from '../services/api';

interface QuestionPreviewProps {
  question: Question;
  showAnswer?: boolean;
}

const QuestionPreview: React.FC<QuestionPreviewProps> = ({
  question,
  showAnswer = false,
}) => {
  const getQuestionIcon = (type: string) => {
    const icons: Record<string, any> = {
      mcq: CheckCircle2,
      fillBlank: Edit3,
      correction: Edit3,
      sequence: ArrowRight,
      matching: Link2,
      ordering: List,
      flashcard: CreditCard,
      audioDictation: Mic,
      speedChallenge: Zap,
      interview: MessageSquare,
      essay: FileText,
    };
    const Icon = icons[type] || Circle;
    return <Icon className="h-5 w-5" />;
  };

  const getDifficultyColor = (difficulty: string) => {
    const colors: Record<string, string> = {
      easy: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      hard: 'bg-red-100 text-red-800',
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-800';
  };

  const renderQuestionContent = () => {
    switch (question.type) {
      case 'mcq':
        const mcqOptions = Array.isArray(question.options) ? question.options : [];
        return (
          <div className="space-y-2">
            {mcqOptions.map((option: any, idx: number) => {
              const optionText = typeof option === 'object' 
                ? (option.text || JSON.stringify(option)) 
                : String(option);
              const isCorrect = typeof option === 'object' ? option.isCorrect : false;
              return (
                <div
                  key={idx}
                  className={`
                    p-3 rounded border
                    ${
                      showAnswer && isCorrect
                        ? 'bg-green-50 border-green-300'
                        : 'bg-gray-50 border-gray-200'
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    {showAnswer && isCorrect ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Circle className="h-4 w-4 text-gray-400" />
                    )}
                    <span>{optionText}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'fillBlank':
        const blankSentence = typeof question.blank_sentence === 'object' 
          ? JSON.stringify(question.blank_sentence) 
          : (question.blank_sentence || '');
        const blankOptions = Array.isArray(question.blank_options) ? question.blank_options : [];
        const blankCorrect = typeof question.blank_correct === 'object'
          ? JSON.stringify(question.blank_correct)
          : question.blank_correct;
        return (
          <div className="space-y-3">
            <p className="text-gray-700 font-medium">{blankSentence}</p>
            <div className="flex flex-wrap gap-2">
              {blankOptions.map((option, idx) => {
                const optionStr = typeof option === 'object' ? JSON.stringify(option) : String(option);
                return (
                  <span
                    key={idx}
                    className={`
                      px-3 py-1 rounded border
                      ${
                        showAnswer && optionStr === blankCorrect
                          ? 'bg-green-100 border-green-300 text-green-800'
                          : 'bg-gray-100 border-gray-300'
                      }
                    `}
                  >
                    {optionStr}
                  </span>
                );
              })}
            </div>
          </div>
        );

      case 'flashcard':
        const frontText = question.extra_data?.front || question.options?.front_text || '';
        const backText = question.extra_data?.back || question.options?.back_text || '';
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded border border-blue-200">
              <p className="text-sm text-blue-600 font-semibold mb-2">Recto</p>
              <p className="text-gray-800">{typeof frontText === 'object' ? JSON.stringify(frontText) : frontText}</p>
            </div>
            {showAnswer && (
              <div className="p-4 bg-green-50 rounded border border-green-200">
                <p className="text-sm text-green-600 font-semibold mb-2">Verso</p>
                <p className="text-gray-800">{typeof backText === 'object' ? JSON.stringify(backText) : backText}</p>
              </div>
            )}
          </div>
        );

      case 'matching':
        const matchingPairs = question.options?.matching_pairs || question.options?.pairs || [];
        return (
          <div className="space-y-2">
            {matchingPairs.map((pair: any, idx: number) => (
              <div
                key={idx}
                className="flex items-center gap-4 p-2 bg-gray-50 rounded"
              >
                <span className="flex-1">{typeof pair.left === 'object' ? JSON.stringify(pair.left) : pair.left}</span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                {showAnswer ? (
                  <span className="flex-1 text-green-600 font-medium">
                    {typeof pair.right === 'object' ? JSON.stringify(pair.right) : pair.right}
                  </span>
                ) : (
                  <span className="flex-1 text-gray-400">???</span>
                )}
              </div>
            ))}
          </div>
        );

      case 'sequence':
        const sequenceItems = question.options?.sequence || [];
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              {sequenceItems.map((item: any, idx: number) => (
                <span
                  key={idx}
                  className="px-3 py-2 bg-blue-50 border border-blue-200 rounded"
                >
                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                </span>
              ))}
            </div>
            {showAnswer && (
              <p className="text-green-600 font-medium">
                Réponse: {typeof question.correct_answer === 'object' ? JSON.stringify(question.correct_answer) : question.correct_answer}
              </p>
            )}
          </div>
        );

      case 'correction':
        const incorrectSentence = question.options?.incorrect_sentence || '';
        return (
          <div className="space-y-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-600 font-semibold mb-1">
                Phrase incorrecte:
              </p>
              <p className="text-gray-800">{typeof incorrectSentence === 'object' ? JSON.stringify(incorrectSentence) : incorrectSentence}</p>
            </div>
            {showAnswer && (
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-600 font-semibold mb-1">
                  Correction:
                </p>
                <p className="text-gray-800">{typeof question.correct_answer === 'object' ? JSON.stringify(question.correct_answer) : question.correct_answer}</p>
              </div>
            )}
          </div>
        );

      case 'ordering':
        const orderItems = question.options?.order_items || question.options?.items || [];
        return (
          <div className="space-y-2">
            {orderItems.map((item: any, idx: number) => (
              <div key={idx} className="p-2 bg-gray-50 border border-gray-200 rounded">
                {showAnswer && <span className="text-gray-500 mr-2">{idx + 1}.</span>}
                {typeof item === 'object' ? JSON.stringify(item) : String(item)}
              </div>
            ))}
            {showAnswer && (
              <p className="text-sm text-green-600 mt-2">
                {typeof question.correct_answer === 'object' ? JSON.stringify(question.correct_answer) : question.correct_answer}
              </p>
            )}
          </div>
        );

      case 'essay':
        const essayCorrectAnswer = typeof question.correct_answer === 'object' 
          ? JSON.stringify(question.correct_answer) 
          : question.correct_answer;
        const essayCriteria = question.extra_data?.evaluation_criteria;
        return (
          <div className="space-y-3">
            {question.extra_data && (
              <div className="text-sm text-gray-600 space-y-1">
                <p>Longueur: {question.extra_data.min_words} - {question.extra_data.max_words} mots</p>
                {essayCriteria && (
                  <p>Critères: {Array.isArray(essayCriteria) ? essayCriteria.join(', ') : String(essayCriteria)}</p>
                )}
              </div>
            )}
            {showAnswer && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-600 font-semibold mb-1">
                  Points clés attendus:
                </p>
                <p className="text-gray-800">{essayCorrectAnswer}</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <p className="text-gray-600">
            Type de question: <span className="font-medium">{question.type}</span>
          </p>
        );
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-blue-600">{getQuestionIcon(question.type)}</span>
          <span className="font-semibold text-gray-900">
            Niveau {question.order}
          </span>
        </div>
        <div className="flex gap-2">
          <span
            className={`px-2 py-1 text-xs rounded ${getDifficultyColor(
              question.difficulty
            )}`}
          >
            {question.difficulty}
          </span>
          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
            {question.type}
          </span>
        </div>
      </div>

      {/* Topic */}
      {question.topic && (
        <p className="text-sm text-gray-500 mb-3">Thème: {question.topic}</p>
      )}

      {/* Question Text */}
      <div className="mb-4">
        <p className="text-gray-800 font-medium mb-3">
          {typeof question.text === 'object' ? JSON.stringify(question.text) : question.text}
        </p>
        {renderQuestionContent()}
      </div>

      {/* Hint */}
      {question.hint && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            💡 {typeof question.hint === 'object' ? JSON.stringify(question.hint) : question.hint}
          </p>
        </div>
      )}

      {/* Explanation */}
      {showAnswer && (
        <div className="mt-4 p-3 bg-gray-50 border-l-4 border-blue-500 rounded">
          <p className="text-sm font-semibold text-gray-700 mb-1">
            📚 Explication
          </p>
          <p className="text-sm text-gray-600">
            {typeof question.explanation === 'object' ? JSON.stringify(question.explanation) : question.explanation}
          </p>
        </div>
      )}
    </div>
  );
};

export default QuestionPreview;
